import { BodyText, Button, Chip, EmailIcon, FormControl, Input } from '@farol-ds/react'
import classNames from 'classnames'

import {
  ENTITY_NOTICE_CHIP_RECOMMENDED,
  ENTITY_NOTICE_EMAIL_HELP_TEXT,
  ENTITY_NOTICE_EMAIL_LABEL,
  ENTITY_NOTICE_LEGAL_LINK_LABELS,
  ENTITY_NOTICE_LEGAL_TEXT_PREFIX,
  ENTITY_NOTICE_PICKER_GOOGLE_OPTION_LABEL,
  ENTITY_NOTICE_PICKER_OTHER_EMAIL_OPTION_LABEL,
  ENTITY_NOTICE_PICKER_TITLE_TEMPLATE,
  resolveEntityNoticeCopy,
} from '../../experiments/entity-notice-fakedoor'
import { AuthFooter } from '../auth-footer'
import { LoginCard } from '../login-card'
import styles from './entity-notice-entry.module.scss'
import type { EntityNoticeEntryProps } from './entity-notice-entry.types'
import pickerStyles from './entity-notice-entry-picker.module.scss'
import { EntityNoticeGooglePickerButton } from './entity-notice-google-picker-button'
import { EntityNoticeTitle } from './entity-notice-title'
import { useEntityNoticeEmailContinue } from './use-entity-notice-email-continue'

export type EntityNoticeEntryPickerProps = Omit<EntityNoticeEntryProps, 'variant'>

/**
 * Cell B's own shape — no WhatsApp field, no "ou" divider: a two-card
 * picker instead of the e-mail-first flow, per diff line 6 (Invariante 3
 * rewritten). "No card selected → 'Em outro email' selected → e-mail field
 * appears" is the same reveal/validate/submit machine
 * `EntityNoticeEntryEmailFirst` uses, kept separate from render.
 */
export function EntityNoticeEntryPicker({
  entityName,
  onSubmit,
  isLoading = false,
  error,
  onCloseClick,
  appId,
  featureCode,
  fromComponent,
  nextUrl,
}: EntityNoticeEntryPickerProps) {
  const {
    isEmailVisible,
    emailValue,
    emailError,
    revealEmail,
    setEmailValue,
    clearEmailError,
    handleContinue,
  } = useEntityNoticeEmailContinue({ onSubmit })

  const copy = resolveEntityNoticeCopy('variantB', isEmailVisible ? 'show-email' : 'entry')

  return (
    <LoginCard
      header={{
        align: 'center',
        title: (
          <EntityNoticeTitle
            template={ENTITY_NOTICE_PICKER_TITLE_TEMPLATE}
            entityName={entityName || ''}
          />
        ),
      }}
      onCloseClick={onCloseClick}
    >
      <div className={styles.container}>
        <div className={pickerStyles.cards}>
          <div className={pickerStyles.card}>
            <EntityNoticeGooglePickerButton
              label={ENTITY_NOTICE_PICKER_GOOGLE_OPTION_LABEL}
              appId={appId}
              featureCode={featureCode}
              fromComponent={fromComponent}
              nextUrl={nextUrl}
              className={pickerStyles.cardGoogleButton}
              chip={
                <Chip size="sm" kind="secondary">
                  {ENTITY_NOTICE_CHIP_RECOMMENDED}
                </Chip>
              }
            />
          </div>

          <button
            type="button"
            className={classNames(pickerStyles.card, pickerStyles.cardButton, {
              [pickerStyles.cardSelected]: isEmailVisible,
            })}
            aria-pressed={isEmailVisible}
            onClick={revealEmail}
          >
            <EmailIcon className={pickerStyles.cardIcon} />
            <BodyText asChild>
              <span>{ENTITY_NOTICE_PICKER_OTHER_EMAIL_OPTION_LABEL}</span>
            </BodyText>
          </button>
        </div>

        {isEmailVisible && (
          <FormControl error={Boolean(emailError)}>
            <FormControl.Label>{ENTITY_NOTICE_EMAIL_LABEL}</FormControl.Label>
            <FormControl.Description>{ENTITY_NOTICE_EMAIL_HELP_TEXT}</FormControl.Description>
            <Input
              type="email"
              name="email"
              autoComplete="email"
              value={emailValue}
              onChange={(e) => {
                setEmailValue(e.target.value)
                if (emailError) clearEmailError()
              }}
            />
            {emailError && <FormControl.ErrorMessage>{emailError}</FormControl.ErrorMessage>}
          </FormControl>
        )}

        {error && (
          <FormControl error>
            <FormControl.ErrorMessage>{error}</FormControl.ErrorMessage>
          </FormControl>
        )}

        <div className={styles.actionsGroup}>
          <Button
            type="button"
            kind="primary"
            disabled={isLoading}
            loading={isLoading}
            onClick={handleContinue}
          >
            {copy.ctaText}
          </Button>

          <AuthFooter
            textPrefix={ENTITY_NOTICE_LEGAL_TEXT_PREFIX}
            textComponent="body"
            linkLabels={ENTITY_NOTICE_LEGAL_LINK_LABELS}
            linkUnderline
            trailingPeriod
          />
        </div>
      </div>
    </LoginCard>
  )
}
